import { useParams } from "@tanstack/react-router";
import { GraduationCap, Pencil, Tag } from "lucide-react";
import { useState } from "react";
import { ActivityLog } from "@/components/shared/activity-log";
import { DetailSection, MetadataRow } from "@/components/shared/detail-section";
import { type DetailMetaItem, EntityDetailHeader } from "@/components/shared/entity-detail-header";
import { EntityDetailPage } from "@/components/shared/entity-detail-page";
import { LifecycleBadge } from "@/components/shared/lifecycle-badge";
import { RequiredByRolesSection } from "@/components/shared/required-by-roles-section";
import { TabBar, type TabItem } from "@/components/shared/tab-bar";
import { SkillFormDialog } from "@/components/skills/SkillFormDialog";
import { SkillPeopleSection } from "@/components/skills/SkillPeopleSection";
import { Button } from "@/components/ui/button";
import { useSkill } from "@/hooks/use-skills";
import { useSession } from "@/lib/auth-client";
import { canEditAll } from "@/lib/permissions";

type TabKey = "overview" | "people" | "history";

const TABS: TabItem[] = [
  { key: "overview", label: "Overview" },
  { key: "people", label: "People" },
  { key: "history", label: "Change history" },
];

export function SkillDetailPage() {
  const params = useParams({ strict: false });
  const skillId = typeof params.skillId === "string" ? params.skillId : "";
  const query = useSkill(skillId);
  const { data: session } = useSession();
  const isAdmin = canEditAll(session?.user?.role);
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState<TabKey>("overview");

  return (
    <EntityDetailPage
      query={query}
      entityLabel="Skill"
      section="Capability"
      list={{ label: "Skills", to: "/dashboard/skills" }}
      name={query.data?.name}
    >
      {(skill) => {
        const categoryLabel = skill.categories.map((c) => c.name).join(", ") || null;
        const meta: DetailMetaItem[] = [];
        if (categoryLabel) meta.push({ key: "category", icon: Tag, node: categoryLabel });

        return (
          <>
            <EntityDetailHeader
              visual={
                <span className="flex size-20 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                  <GraduationCap className="size-8" />
                </span>
              }
              title={skill.name}
              subtitle={skill.description}
              meta={meta}
              badges={<LifecycleBadge status={skill.status} />}
              actions={
                isAdmin && (
                  <Button size="sm" onClick={() => setEditing(true)}>
                    <Pencil className="mr-1.5 size-3.5" />
                    Edit
                  </Button>
                )
              }
            />

            <TabBar tabs={TABS} value={tab} onChange={(k) => setTab(k as TabKey)} />

            {tab === "overview" && (
              <>
                <DetailSection title="Overview">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <MetadataRow label="Categories" value={categoryLabel} />
                    <MetadataRow label="Definition" value={skill.description} />
                  </div>
                </DetailSection>
                <DetailSection title="Required by roles">
                  <RequiredByRolesSection
                    roles={skill.roles}
                    originKind="skill"
                    entityId={skill.id}
                    entityName={skill.name}
                  />
                </DetailSection>
              </>
            )}

            {tab === "people" && (
              <DetailSection title="People with this skill">
                <p className="mb-3 text-sm text-muted-foreground">
                  Everyone who holds a role that requires this skill.
                </p>
                <SkillPeopleSection skillId={skill.id} skillName={skill.name} />
              </DetailSection>
            )}

            {tab === "history" && (
              <DetailSection title="Change history">
                <ActivityLog
                  entityType="skill"
                  entityId={skill.id}
                  fieldLabels={{
                    // Historical diff key from before the category/theme
                    // merge — never rewritten, see .claude/rules/api.md.
                    categoryId: "Category",
                    categories: "Categories",
                    // Diff keys stay `level_<n>` — historical rows carry them and are
                    // never rewritten. Only the label follows the WI-103 rename.
                    level_1: "Rating 1",
                    level_2: "Rating 2",
                    level_3: "Rating 3",
                    level_4: "Rating 4",
                    level_5: "Rating 5",
                  }}
                />
              </DetailSection>
            )}

            {isAdmin && (
              <SkillFormDialog
                key={skill.updatedAt}
                open={editing}
                onOpenChange={setEditing}
                skill={skill}
              />
            )}
          </>
        );
      }}
    </EntityDetailPage>
  );
}
